"use client";

import { useQuery } from "@tanstack/react-query";
import { ThetaError, type GroupBotTrace } from "@theta/api-client";
import { thetaApi, dataOf } from "@/lib/api/theta";
import { retryUnlessRefused } from "@/lib/hooks/queryRetry";

/**
 * THE TRANSCRIPT BEHIND ONE BOT REPLY — the modal on a bot post, comment or
 * message. The group-wide list (recent attempts, for the bot admin page) is
 * `useGroupBotTraces` in `useGroupBot.ts`; both serve the same `GroupBotTrace`.
 *
 * `null` IS A NORMAL RESULT, NOT AN ERROR, and that is the whole reason this
 * hook exists rather than a bare `useQuery`. The route 404s for every reply
 * with no transcript — anything a person wrote, and any bot reply from before
 * traces named their reply — and the caller draws the control from
 * `author.isBot` alone, without the content payload carrying a "has one" flag.
 * So a 404 here is the answer "no transcript", rendered as such. Every OTHER
 * status still throws, so a real failure is not silently drawn as an absence.
 */
function traceOrNull(p: Promise<GroupBotTrace>): Promise<GroupBotTrace | null> {
  return p.catch((err: unknown) => {
    if (err instanceof ThetaError && err.status === 404) return null;
    throw err;
  });
}

export function useBotTraceForPost(postId: number, enabled: boolean) {
  return useQuery({
    queryKey: ["v1", "post", postId, "bot", "trace"] as const,
    queryFn: () =>
      traceOrNull(
        thetaApi.http
          .GET("/posts/{id}/bot/trace", { params: { path: { id: postId } } })
          .then(dataOf<GroupBotTrace>),
      ),
    enabled,
    // A written transcript never changes — the job writes the row once and
    // nothing updates it. There is nothing to refetch for.
    staleTime: Infinity,
    // A 401 is the steady state for a signed-out reader since OSO-695 — a
    // refusal is an answer, not a blink, so it is not asked twice. (The 404
    // never reaches this: `traceOrNull` has already made it a result.)
    retry: retryUnlessRefused,
  });
}

export function useBotTraceForMessage(messageId: number, enabled: boolean) {
  return useQuery({
    queryKey: ["v1", "message", messageId, "bot", "trace"] as const,
    queryFn: () =>
      traceOrNull(
        thetaApi.http
          .GET("/messages/{id}/bot/trace", { params: { path: { id: messageId } } })
          .then(dataOf<GroupBotTrace>),
      ),
    enabled,
    staleTime: Infinity,
    // As above: a refusal is an answer, not a failure worth a second ask.
    retry: retryUnlessRefused,
  });
}
